/**
 * Lazy-loading wrapper for Zod validation library
 * 
 * Reduces initial bundle size by ~15KB by deferring Zod import
 * until first form validation is needed.
 * 
 * Usage:
 * ```ts
 * import { getZod } from '@/lib/zodLoader';
 * 
 * const z = await getZod();
 * const schema = z.object({ ... });
 * ```
 */

import type { z as ZodType } from 'zod';

let zodInstance: typeof ZodType | null = null;
let zodPromise: Promise<typeof ZodType> | null = null;

/**
 * Get Zod instance (lazy-loaded on first call)
 * 
 * @returns Promise resolving to Zod library
 * 
 * @example
 * ```ts
 * const z = await getZod();
 * const EmailSchema = z.string().email();
 * ```
 */
export async function getZod(): Promise<typeof ZodType> {
  // Return cached instance if already loaded
  if (zodInstance) return zodInstance;

  // Return existing promise if load is in progress (prevents duplicate imports)
  if (zodPromise) return zodPromise;

  // Start loading Zod
  zodPromise = import('zod').then((mod) => {
    zodInstance = mod.z;
    return mod.z;
  });

  return zodPromise;
}

/**
 * Preload Zod library without blocking
 * 
 * Call this on user interaction (hover, focus) to prefetch
 * before form validation is needed.
 * 
 * @example
 * ```tsx
 * <form onMouseEnter={() => preloadZod()}>
 * ```
 */
export function preloadZod(): void {
  if (!zodInstance && !zodPromise) {
    getZod().catch(() => {
      // Silent fail — will retry on actual usage
    });
  }
}

/**
 * Re-export Zod types for convenience
 * 
 * These are type-only imports and don't affect bundle size.
 */
export type { z } from 'zod';
export type { ZodType, ZodSchema, ZodTypeAny, ZodIssue } from 'zod';